import React, { useCallback, useEffect, useState } from "react";
import { Check, RefreshCw } from "lucide-react";

import { AdminScreen, Chips, Field, ListEditor, OPTS, Section, Toggle, UploadButton } from "@/admin/kit";
import { api, relUrl } from "@/lib/api";
import { Button, Spinner } from "@/components/kit";
import { formatMoney } from "@/components/shopui";
import { useShop } from "@/shop";
import { useApp } from "@/store";

const EDIT = ["name_override", "description_override", "suitable_hair_types", "hold", "finish", "ingredients", "directions", "amount", "application", "shopify_handle", "image_override", "active"];

function Editor({ p, store, onSaved }) {
  const { toast } = useApp();
  const [d, setD] = useState(() => Object.fromEntries(EDIT.map((k) => [k, p[k]])));
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setD((x) => ({ ...x, [k]: v }));
  const live = p.shopify;
  const save = async () => {
    setBusy(true);
    try {
      await api(`/admin/products/${p.slug}`, { method: "PUT", body: { ...d, image_override: relUrl(d.image_override || ""), application: (d.application ?? []).filter((x) => x.trim()) } });
      toast(`${p.slug} saved — live in the app now.`, "success");
      onSaved();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };
  const id = `admin-product-${p.slug}`;
  return (
    <Section testId={id} title={d.name_override || live?.title || p.name} note={`slug: ${p.slug}`}>
      <div data-testid={`${id}-live`} className="flex flex-col gap-0.5 rounded-xl bg-tert p-3">
        <span className="t-label">Live from Shopify (read-only)</span>
        {live ? <span className="t-strong">{live.title} · {formatMoney(live.price)} · {live.available ? "In stock" : "Sold out"}</span> : <span className="t-body !text-[#EF4444]">Not linked or Shopify unavailable</span>}
        <span className="t-caption">Prices and stock are managed in Shopify, not here.</span>
      </div>
      <Toggle testId={`${id}-active`} label="Active (shown in the app)" value={!!d.active} onChange={set("active")} />
      <Chips testId={`${id}-handle`} single label="Linked Shopify product" options={store.map((s) => s.handle)} value={[d.shopify_handle]} onChange={(x) => set("shopify_handle")(x[0])} />
      <Field testId={`${id}-name`} label="Name override (blank = Shopify title)" value={d.name_override} onChange={set("name_override")} />
      <Field testId={`${id}-description`} label="Description override (blank = Shopify)" value={d.description_override} onChange={set("description_override")} multiline />
      <Chips testId={`${id}-types`} label="Suitable hair types" options={OPTS.hair_types} value={d.suitable_hair_types} onChange={set("suitable_hair_types")} />
      <Chips testId={`${id}-hold`} single label="Hold" options={OPTS.hold} value={[d.hold]} onChange={(x) => set("hold")(x[0])} />
      <Chips testId={`${id}-finish`} single label="Finish" options={OPTS.finish} value={[d.finish]} onChange={(x) => set("finish")(x[0])} />
      <Field testId={`${id}-ingredients`} label="Ingredients" value={d.ingredients} onChange={set("ingredients")} multiline />
      <Field testId={`${id}-directions`} label="Directions" value={d.directions} onChange={set("directions")} multiline />
      <Field testId={`${id}-amount`} label="How much to use" value={d.amount} onChange={set("amount")} />
      <ListEditor testId={`${id}-application`} label="Application tips" items={d.application} onChange={set("application")} numbered />
      <div className="flex flex-col gap-2">
        <span className="t-label">Image override (optional)</span>
        {d.image_override ? <img src={d.image_override} alt="" className="h-24 w-24 rounded-xl object-cover" /> : null}
        <div className="flex flex-wrap gap-2">
          <UploadButton kind="image" testId={`${id}-image-upload`} title="Upload image" onUploaded={(u) => set("image_override")(u.url)} onError={(m) => toast(m, "error")} />
          {d.image_override ? <Button testId={`${id}-image-clear`} title="Use Shopify image" variant="ghost" onClick={() => set("image_override")("")} className="!min-h-[44px]" /> : null}
        </div>
      </div>
      <Button testId={`${id}-save`} title="Save product" icon={Check} loading={busy} onClick={save} />
    </Section>
  );
}

export default function AdminProducts() {
  const { toast } = useApp();
  const { loadCatalog } = useShop();
  const [r, setR] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const load = useCallback(() => {
    api("/admin/products").then(setR).catch((e) => toast(e.message, "error"));
  }, [toast]);
  useEffect(load, [load]);
  const resync = async () => {
    setSyncing(true);
    try {
      const s = await api("/admin/products/resync", { method: "POST" });
      toast(s.live ? "Re-synced from Shopify." : s.error ?? "Shopify unavailable", s.live ? "success" : "error");
      load();
      loadCatalog?.();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSyncing(false);
    }
  };
  return (
    <AdminScreen title="Products" testId="admin-products" right={<Button testId="admin-products-resync" title="Re-sync" icon={RefreshCw} variant="outline" loading={syncing} onClick={resync} className="!min-h-[44px] !px-4" />}>
      {!r ? <Spinner /> : (
        <>
          <p data-testid="admin-products-live" className="t-caption">{r.live ? `Shopify live · last fetched ${r.fetched_at ? new Date(r.fetched_at * 1000).toLocaleTimeString() : "—"}` : r.error}</p>
          <div className="grid items-start gap-5 md:grid-cols-2">
            {r.products.map((p) => <Editor key={p.slug} p={p} store={r.store_products} onSaved={() => { load(); loadCatalog?.(); }} />)}
          </div>
        </>
      )}
    </AdminScreen>
  );
}
